<template>
  <v-dialog :value="open" persistent fullscreen scrollable>
    <v-card class="presupuesto-dialog-card">
      <v-toolbar flat color="primary" class="mb-2" style="flex-shrink: 0">
        <v-toolbar-title class="white--text">{{ pageTitle }}</v-toolbar-title>
        <v-spacer />
        <v-toolbar-items>
          <v-btn icon text class="white--text" @click="$emit('close')">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>

      <v-card-text v-if="errorMessage" class="pa-2" style="flex-shrink: 0; flex-grow: 0">
        <v-alert type="error" dense class="mb-0">{{ errorMessage }}</v-alert>
      </v-card-text>

      <v-card-text class="presupuesto-form-area pa-2">
        <presupuesto-pedido-form
          ref="formRef"
          :initial-items="formInitialItems"
          :initial-cliente-id="formInitialClienteId"
          :initial-vendedor-id="formInitialVendedorId"
          :deposito-id="depositoIdSeleccionado"
          :tipo-documento-fijo="tipoDocumentoFijo"
          :initial-descuento="formInitialDescuento"
          :initial-recargo="formInitialRecargo"
          :initial-lista-precios-venta-id="formInitialListaPreciosVentaId"
          :cart-table-height="'calc(100vh - 300px)'"
          @update:tipoDocumento="tipoDocumento = $event"
          @total-change="totalInfo = $event"
        />
      </v-card-text>

      <v-divider />
      <v-card-actions class="footer-actions px-4">
        <v-btn text color="grey darken-1" @click="$emit('close')">
          <v-icon left small>mdi-close</v-icon>
          Cerrar
        </v-btn>
        <v-spacer />
        <div style="display:flex;flex-direction:row;align-items:center">
          <template v-if="totalInfo.descuento > 0 || totalInfo.recargo > 0">
            <div style="display:flex;flex-direction:column;align-items:flex-end;gap:4px">
              <div style="display:flex;gap:4px">
                <v-chip v-if="totalInfo.descuento > 0" small color="green darken-1" dark>-{{ totalInfo.descuento }}%</v-chip>
                <v-chip v-if="totalInfo.recargo > 0" small color="red darken-1" dark>+{{ totalInfo.recargo }}%</v-chip>
              </div>
              <span style="font-size:0.92rem;color:#b0bec5;text-decoration:line-through">${{ totalInfo.subtotal }}</span>
            </div>
            <v-icon color="grey lighten-1" style="margin:0 8px">mdi-arrow-right</v-icon>
          </template>
          <div style="display:flex;flex-direction:column;align-items:center;line-height:1.3">
            <span style="font-size:0.68rem;text-transform:uppercase;letter-spacing:0.6px;color:#90a4ae;font-weight:600">Total a Pagar</span>
            <span style="font-size:1.9rem;font-weight:800;color:#0277bd;letter-spacing:-0.5px;line-height:1.1">${{ totalInfo.total }}</span>
          </div>
        </div>
        <v-spacer />
        <v-btn color="primary" :loading="loading" depressed @click="create">
          <v-icon left small>mdi-check</v-icon>
          Enviar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import PresupuestoPedidoForm from "../../../../ventas/pages/PresupuestoPedidoDesdeProductosPage/PresupuestoPedidoForm.vue";
import UserConfigurationProvider from "../../../../user-configuration/providers/UserConfigurationProvider";
import PresupuestoProvider from "../../../../ventas/providers/PresupuestoProvider";
import OrdenVentaProvider from "../../../../ventas/providers/OrdenVentaProvider";
import ProductoProvider from "../../../../producto/providers/ProductoProvider";

export default {
  name: "PresupuestoPedidoDesdeProductosCreate",
  components: { PresupuestoPedidoForm },
  props: {
    open: { type: Boolean, default: false },
    items: { type: Array, default: () => [] },
    /** 'presupuesto' | 'pedido' para fijar el tipo desde menú Presupuestos o Pedidos. */
    tipoDocumentoFijo: { type: String, default: null },
    /** Si está definido, modo edición: cargar presupuesto y actualizar en lugar de crear. */
    presupuestoId: { type: String, default: null },
    /** Si está definido y tipoDocumento es 'pedido', modo edición: cargar OV y actualizar. */
    ordenVentaId: { type: String, default: null },
  },
  data() {
    return {
      tipoDocumento: null,
      depositoIdSeleccionado: null,
      loading: false,
      errorMessage: "",
      formInitialItems: [],
      formInitialClienteId: null,
      formInitialVendedorId: null,
      formInitialDescuento: 0,
      formInitialRecargo: 0,
      formInitialListaPreciosVentaId: null,
      totalInfo: { total: "0.00", subtotal: "0.00", descuento: 0, recargo: 0 },
    };
  },
  computed: {
    pageTitle() {
      if (this.presupuestoId) return "Modificando Presupuesto";
      if (this.ordenVentaId) return "Modificando Orden de Venta";
      const tipo = this.tipoDocumento || this.tipoDocumentoFijo || "presupuesto";
      return tipo === "pedido" ? "Creando Orden de Venta" : "Creando Presupuesto";
    },
  },
  watch: {
    open: {
      immediate: true,
      handler(v) {
        if (v) {
          this.tipoDocumento = this.tipoDocumentoFijo || "presupuesto";
          this.loadDeposito();
          if (this.presupuestoId) {
            this.loadPresupuestoForEdit();
          } else if (this.ordenVentaId) {
            this.loadOrdenVentaForEdit();
          } else {
            this.formInitialItems = [...(this.items || [])];
            this.formInitialClienteId = null;
            this.formInitialVendedorId = null;
            this.formInitialDescuento = 0;
            this.formInitialRecargo = 0;
            this.formInitialListaPreciosVentaId = null;
          }
        }
      },
    },
  },
  methods: {
    async loadDeposito() {
      try {
        let depositoId = UserConfigurationProvider.getDepositoIdFromCache();
        if (!depositoId) {
          const r = await UserConfigurationProvider.getUserConfiguration(true);
          const config = r.data && r.data.userConfigurationGet;
          depositoId =
            (config && config.depositoIdSeleccionado) ||
            (config && config.deposito && config.deposito.id) ||
            null;
        }
        this.depositoIdSeleccionado = depositoId;
      } catch (e) {
        console.error(e);
        this.depositoIdSeleccionado = null;
      }
    },
    async loadPresupuestoForEdit() {
      if (!this.presupuestoId) return;
      this.loading = true;
      this.errorMessage = "";
      try {
        const r = await PresupuestoProvider.findPresupuesto(this.presupuestoId);
        const presupuesto = (r.data && r.data.presupuestoFind) || null;
        if (!presupuesto) {
          this.errorMessage = "Presupuesto no encontrado.";
          this.formInitialItems = [];
          this.formInitialClienteId = null;
          return;
        }
        if (presupuesto.estado === "PEDIDO_CREADO") {
          this.errorMessage = "Este presupuesto ya fue confirmado (se creó el pedido) y no se puede modificar.";
          this.formInitialItems = [];
          this.formInitialClienteId = null;
          return;
        }
        this.formInitialClienteId =
          (presupuesto.clienteId && (presupuesto.clienteId.id || presupuesto.clienteId)) ||
          (presupuesto.cliente && presupuesto.cliente.id) ||
          null;
        this.formInitialVendedorId =
          (presupuesto.vendedorId && (presupuesto.vendedorId.id || presupuesto.vendedorId)) ||
          (presupuesto.vendedor && presupuesto.vendedor.id) ||
          null;
        this.formInitialDescuento = presupuesto.descuento != null ? Number(presupuesto.descuento) || 0 : 0;
        this.formInitialRecargo = presupuesto.recargo != null ? Number(presupuesto.recargo) || 0 : 0;
        this.formInitialListaPreciosVentaId = presupuesto.listaPreciosVentaId || null;
        const items = presupuesto.items || [];
        if (items.length === 0) {
          this.formInitialItems = [];
          return;
        }
        const productIds = [...new Set(items.map((i) => i.producto && (i.producto.id || i.producto)).filter(Boolean))];
        const productResponses = await Promise.all(
          productIds.map((id) => ProductoProvider.findProducto(id))
        );
        const productMap = {};
        productResponses.forEach((res) => {
          const p = res.data && res.data.productoFind;
          if (p && (p.id || p._id)) productMap[p.id || p._id] = p;
        });
        this.formInitialItems = items.map((item) => {
          const productId = item.producto && (item.producto.id || item.producto);
          let producto = productMap[productId] || null;
          if (producto && item.precioBase != null && Number(item.precioBase) > 0) {
            producto = { ...producto, precio: Number(item.precioBase), precioVenta: Number(item.precioBase) };
          }
          const snap = item.ofertaSnapshot;
          const ofertaAplicada =
            snap && snap.nombre
              ? {
                  nombre: snap.nombre,
                  tipo: snap.tipo || null,
                  valor: snap.valor != null ? snap.valor : null,
                  descuento: snap.descuentoAplicadoMonto != null ? Number(snap.descuentoAplicadoMonto) : 0,
                }
              : null;
          return {
            producto,
            product: producto,
            cantidad: Math.max(0, Number(item.cantidad) || 0),
            ofertaAplicada,
            precioManual: !!item.precioManual,
          };
        });
      } catch (e) {
        console.error(e);
        this.errorMessage = (e && e.message) || "Error al cargar el presupuesto.";
        this.formInitialItems = [];
        this.formInitialClienteId = null;
      } finally {
        this.loading = false;
      }
    },
    async loadOrdenVentaForEdit() {
      if (!this.ordenVentaId) return;
      this.loading = true;
      this.errorMessage = "";
      try {
        const r = await OrdenVentaProvider.findOrdenVenta(this.ordenVentaId);
        const orden = (r.data && r.data.ordenVentaFind) || null;
        if (!orden) {
          this.errorMessage = "Orden de venta no encontrada.";
          this.formInitialItems = [];
          this.formInitialClienteId = null;
          return;
        }
        if (orden.estado !== "CONFIRMADO") {
          this.errorMessage = "Esta orden de venta no se puede modificar (solo estado CONFIRMADO).";
          this.formInitialItems = [];
          this.formInitialClienteId = null;
          return;
        }
        if (orden.estadoPago && orden.estadoPago !== "NO_PAGADO") {
          this.errorMessage = "Esta orden de venta tiene pagos y no se puede modificar.";
          this.formInitialItems = [];
          this.formInitialClienteId = null;
          return;
        }
        this.formInitialClienteId =
          (orden.clienteId && (orden.clienteId.id || orden.clienteId)) ||
          (orden.cliente && orden.cliente.id) ||
          null;
        this.formInitialVendedorId =
          (orden.vendedorId && (orden.vendedorId.id || orden.vendedorId)) ||
          (orden.vendedor && orden.vendedor.id) ||
          null;
        this.formInitialDescuento = orden.descuento != null ? Number(orden.descuento) || 0 : 0;
        this.formInitialRecargo = orden.recargo != null ? Number(orden.recargo) || 0 : 0;
        this.formInitialListaPreciosVentaId = orden.listaPreciosVentaId || null;

        const items = orden.items || [];
        if (items.length === 0) {
          this.formInitialItems = [];
          return;
        }
        // OrdenVentaFind suele venir con items.producto populado; si no, resolvemos por API.
        const productIds = [...new Set(items.map((i) => i.producto).filter(Boolean).map((p) => p.id || p._id || p))];
        const needFetch = items.some((i) => !(i.producto && typeof i.producto === "object"));
        let productMap = {};
        if (needFetch && productIds.length) {
          const productResponses = await Promise.all(productIds.map((id) => ProductoProvider.findProducto(id)));
          productResponses.forEach((res) => {
            const p = res.data && res.data.productoFind;
            if (p && (p.id || p._id)) productMap[p.id || p._id] = p;
          });
        }

        this.formInitialItems = items.map((item) => {
          const pRaw = item.producto;
          let producto = pRaw && typeof pRaw === "object" ? pRaw : productMap[pRaw] || null;
          if (producto && item.precioBase != null && Number(item.precioBase) > 0) {
            producto = { ...producto, precio: Number(item.precioBase), precioVenta: Number(item.precioBase) };
          }
          return {
            producto,
            product: producto,
            cantidad: Math.max(0, Number(item.cantidad) || 0),
            ofertaAplicada: null,
            precioManual: !!item.precioManual,
          };
        });
      } catch (e) {
        console.error(e);
        this.errorMessage = (e && e.message) || "Error al cargar la orden de venta.";
        this.formInitialItems = [];
        this.formInitialClienteId = null;
      } finally {
        this.loading = false;
      }
    },
    async create() {
      const form = this.$refs.formRef;
      if (!form) return;
      if (!form.valid()) {
        this.errorMessage = form.pedidoExcedeStock
          ? "En la orden de venta la cantidad de cada producto no puede superar el stock disponible."
          : "Complete los campos requeridos (cliente y al menos un producto con cantidad).";
        return;
      }
      if (!this.depositoIdSeleccionado) {
        this.errorMessage = "Seleccione un depósito en su configuración de usuario.";
        return;
      }
      this.loading = true;
      this.errorMessage = "";
      try {
        const payload = form.getPayload();
        const {
          tipoDocumento,
          clienteId,
          vendedorId,
          items: itemsPayload,
          descuento,
          recargo,
          listaPreciosVentaId,
        } = payload;
        const itemsWithQty = (itemsPayload || []).filter((i) => Number(i.cantidad) >= 1);
        if (tipoDocumento === "presupuesto") {
          const input = {
            clienteId,
            depositoId: this.depositoIdSeleccionado,
            vendedorId: vendedorId || undefined,
            descuento,
            recargo,
            listaPreciosVentaId: listaPreciosVentaId || undefined,
            items: itemsWithQty.map((i) => ({
              producto:
                (i.producto && (i.producto.id || i.producto._id)) ||
                (i.product && (i.product.id || i.product._id)),
              cantidad: Number(i.cantidad) || 1,
              precioBase: i.precioBase != null ? Number(i.precioBase) : undefined,
              precioManual: !!i.precioManual,
            })),
          };
          if (this.presupuestoId) {
            const r = await PresupuestoProvider.updatePresupuesto(this.presupuestoId, input);
            if (r.data && r.data.presupuestoUpdate) {
              this.$emit("created", true);
              this.$emit("close");
              this.$router.push("/presupuestos");
            }
          } else {
            const r = await PresupuestoProvider.createPresupuesto(input);
            if (r.data && r.data.presupuestoCreate) {
              this.$emit("created", false);
              this.$emit("close");
              this.$router.push("/presupuestos");
            }
          }
        } else {
          const input = {
            clienteId,
            depositoId: this.depositoIdSeleccionado,
            vendedorId: vendedorId || undefined,
            descuento,
            recargo,
            listaPreciosVentaId: listaPreciosVentaId || undefined,
            items: itemsWithQty.map((i) => {
              const p = i.producto || i.product;
              return {
                producto: (p && (p.id || p._id)) || null,
                cantidad: Number(i.cantidad) || 1,
                precioUnitario: Number(i.precioUnitario) || 0,
                precioTotal: Number(i.precioTotal) || 0,
                productoNombre: (p && p.nombre) || null,
                productoMarca: (p && p.marca && p.marca.nombre) || null,
                productoModelo: (p && p.modelo && p.modelo.nombre) || null,
                productoTamanio: (p && p.tamanio && p.tamanio.tamanio) || null,
                productoIVA: (p && p.ivaPorcentaje != null) ? p.ivaPorcentaje : 0,
                precioBase: i.precioBase != null ? Number(i.precioBase) : undefined,
                precioManual: !!i.precioManual,
              };
            }),
          };
          if (this.ordenVentaId) {
            const r = await OrdenVentaProvider.updateOrdenVenta(this.ordenVentaId, input);
            if (r.data && r.data.ordenVentaUpdate) {
              this.$emit("created", true);
              this.$emit("close");
              this.$router.push("/ordenes-venta");
            }
          } else {
            const r = await OrdenVentaProvider.createOrdenVenta(input);
            if (r.data && r.data.ordenVentaCreate) {
              this.$emit("created", false);
              this.$emit("close");
              this.$router.push("/ordenes-venta");
            }
          }
        }
      } catch (e) {
        console.error(e);
        this.errorMessage =
          (e && e.message) || "Error al crear presupuesto o pedido.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.presupuesto-dialog-card {
  overflow: hidden;
}
.presupuesto-form-area {
  overflow: hidden !important;
  padding: 8px !important;
}
.footer-actions {
  flex-shrink: 0;
  min-height: 56px;
}
</style>
